import { useReducer } from 'react';

import { type IAction } from 'src/utils/ts-utilities';

const steps = [
  'unpaid-invoices', // Show unpaid invoices
  'show-plan-usage-warning', // Warn user about active plan usage
  'review-account-usage', // Prompt to review account usage
  'show-cancellation-prompt', // Emotional cancel prompt
  'show-downgrade-confirmation', // Final confirmation with downgrade warning
  'plan-downgrade-in-progress', // Downgrade is processing
  'discount-request', // Discount request
  'cancellation-success', // Cancellation success
  'keep-plan', // Keep plan
] as const;

export type IBlockingApp = {
  id: string;
  name?: string | null;
  logoUrl?: string | null;
  unclaimed: boolean;
  activeProtectionsCount: number;
};

enum ActionEnum {
  SET_STEP = 'set-step',
  SET_BLOCKING_APPS = 'set-blocking-apps',
  SET_INITIAL_DATA = 'set-initial-data',
}

type IActionReducer =
  | IAction<ActionEnum.SET_INITIAL_DATA>
  | IAction<
      ActionEnum.SET_STEP,
      {
        step: (typeof steps)[number];
      }
    >
  | IAction<
      ActionEnum.SET_BLOCKING_APPS,
      {
        blockingApps: IBlockingApp[];
      }
    >;

type IState = {
  step: (typeof steps)[number] | null;
  blockingApps: IBlockingApp[];
};

const initialState: IState = {
  step: null,
  blockingApps: [],
};

const reducer = (state: IState, action: IActionReducer): IState => {
  switch (action.type) {
    case ActionEnum.SET_STEP:
      return {
        ...state,
        step: action.payload.step,
      };

    case ActionEnum.SET_BLOCKING_APPS:
      return {
        ...state,
        blockingApps: action.payload.blockingApps,
      };

    case ActionEnum.SET_INITIAL_DATA:
      return initialState;
    default:
      return state;
  }
};

export const useCancellationPlanSlice = () => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const setStep = (step: (typeof steps)[number]) => dispatch({ type: ActionEnum.SET_STEP, payload: { step } });

  const setBlockingApps = (blockingApps: IBlockingApp[]) =>
    dispatch({ type: ActionEnum.SET_BLOCKING_APPS, payload: { blockingApps } });

  const setInitialData = () => dispatch({ type: ActionEnum.SET_INITIAL_DATA, payload: {} });

  return {
    ...state,
    setStep,
    setBlockingApps,
    setInitialData,
  };
};
